import { useState } from 'react';
import {
  Box,
  Collapse,
  Flex,
  Icon,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';

import type { QuoteSummaryVehicle } from 'api/quote-marketplace';
import type { ParsedVehicleData } from '../../utils/parseVehicleHtml';
import { formatCurrency } from '../../constants';
import VehicleTableHtml from './VehicleTableHtml';
import { MessageBox } from './MessagesSection';

interface VehicleQuoteCardProps {
  vehicle: QuoteSummaryVehicle;
  parsedData: ParsedVehicleData;
  vehicleIndex: number;
  defaultExpanded?: boolean;
}

const VehicleQuoteCard = ({
  vehicle,
  parsedData,
  vehicleIndex,
  defaultExpanded = true,
}: VehicleQuoteCardProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBg = useColorModeValue('gray.50', 'gray.700');
  const hoverBg = useColorModeValue('gray.100', 'gray.600');

  const vehicleLabel = `${vehicle.vehicle_model_year || ''} ${vehicle.make?.toUpperCase() || ''} ${vehicle.model?.toUpperCase() || ''} (${vehicle.vin || 'No VIN'})`;

  const hasContent =
    parsedData.coverageBreakdownHtml ||
    parsedData.discountsHtml ||
    parsedData.ratingFactorsHtml;

  return (
    <Box mb={4}>
      <Flex
        bg={headerBg}
        px={3}
        py={2}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius={isExpanded ? 'md' : 'md'}
        borderBottomRadius={isExpanded ? 0 : 'md'}
        justify="space-between"
        align="center"
        cursor="pointer"
        _hover={{ bg: hoverBg }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Flex align="center" gap={2}>
          <Icon
            as={isExpanded ? FiChevronDown : FiChevronRight}
            boxSize={4}
            color="gray.500"
          />
          <Text fontWeight="600" fontSize="sm">
            Vehicle {vehicleIndex + 1}: {vehicleLabel}
          </Text>
        </Flex>
        {parsedData.totalPremium != null && (
          <Text fontWeight="600" fontSize="sm">
            {formatCurrency(String(parsedData.totalPremium))}
          </Text>
        )}
      </Flex>

      <Collapse in={isExpanded} animateOpacity>
        <Box
          borderWidth="1px"
          borderTopWidth="0"
          borderColor={borderColor}
          borderBottomRadius="md"
          p={4}
        >
          {hasContent ? (
            <VStack spacing={6} align="stretch">
              {parsedData.coverageBreakdownHtml && (
                <VehicleTableHtml html={parsedData.coverageBreakdownHtml} />
              )}
              {parsedData.discountsHtml && (
                <VehicleTableHtml html={parsedData.discountsHtml} />
              )}
              {parsedData.ratingFactorsHtml && (
                <VehicleTableHtml html={parsedData.ratingFactorsHtml} />
              )}
            </VStack>
          ) : (
            <Text fontSize="sm" color="gray.500" fontStyle="italic">
              No coverage breakdown available for this vehicle
            </Text>
          )}

          <Box mt={hasContent ? 6 : 4}>
            <MessageBox title="Messages" items={vehicle.messages} mb={4} />
            <MessageBox
              title="Discounts"
              items={vehicle.discounts ?? []}
              accent="green"
              mb={0}
            />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default VehicleQuoteCard;
